import { Box, IconButton, styled, SvgIcon, useTheme } from '@mui/material';
import classNames from 'classnames';
import React, { ReactNode, useRef, useState } from 'react';
import Button, { ButtonProps } from '../Button';

export type SeeMoreProps = {
  children: ReactNode;
  minHeight?: string;
  buttonProps: ButtonProps;
  showMoreText?: string;
  showLessText?: string;
  fadeColor?: string;
  linearGradient?: string;
  height?: string;
  buttonColor?: string;
  buttonHoverColor?: string;
};

/**
 * Expandable content display data in expanded or minimized way.
 *
 *
 * ```typescript
 *  import { ExpandableContent } from '@rabex-kit/core';
 *  ```
 * @param props
 * @returns
 */

const Wrapper = styled(Box)(() => ({
  position: 'relative',
  '&.expand': {
    transition: 'height 0.35s ease-in-out'
  }
}));

const ExpanMoreBtn = () => {
  return (
    <SvgIcon
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.2787 5.96875L8.93208 10.3154C8.41875 10.8288 7.57875 10.8288 7.06542 10.3154L2.71875 5.96875"
        stroke="#222531"
        stroke-width="1.125"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
      />
    </SvgIcon>
  );
};

const SeeMore: React.FC<SeeMoreProps> = ({
  buttonProps,
  children,
  showLessText,
  showMoreText,
  minHeight = '100px',
  fadeColor,
  linearGradient,
  height,
  buttonColor = '#E4E7EB',
  buttonHoverColor = '#666666'
}) => {
  const ref = useRef<null | HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();
  const handleClick = () => {
    setIsExpanded(!isExpanded);
    if (isExpanded) handleScroll();
  };

  const handleScroll = () => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const { sx: sxButton, ...otherBProps } = buttonProps;

  return (
    <Wrapper ref={ref} className={classNames({ expand: isExpanded })}>
      <Box
        sx={{
          transition: 'height 0.35s ease-in-out',
          position: 'relative',
          maxHeight: isExpanded ? '100%' : minHeight,
          overflow: 'hidden',
          textAlign: 'start',
          marginBottom: 5,
          '& > p': {
            textAlign: 'start'
          },
          '&:after': {
            content: isExpanded ? 'unset' : '""',
            position: 'absolute',
            zIndex: 100,
            bottom: '0',
            left: '0',
            width: '100%',
            height: '8em', // Fade height for approximately 3 lines
            background: isExpanded
              ? 'transparent'
              : `linear-gradient(180deg, transparent, ${fadeColor})`
          }
        }}
      >
        {children}
      </Box>
      <Button
        {...otherBProps}
        onClick={handleClick}
        sx={{
          display: 'flex',
          mx: 'auto',
          mt: 2,
          borderRadius: 10.5,
          minWidth: 32,
          minHeight: 32,
          padding: 2,
          backgroundColor: buttonColor,
          '&:hover': {
            backgroundColor: buttonHoverColor
          },
          transform: isExpanded ? 'rotate(180deg)' : 'none',
          ...sxButton
        }}
      >
        <ExpanMoreBtn />
      </Button>
    </Wrapper>
  );
};

export default SeeMore;

SeeMore.defaultProps = {
  showLessText: 'مشاهده کمتر',
  showMoreText: 'مشاهده بیشتر',
  fadeColor: '#F7F7F7',
  linearGradient: 'rgba(247, 247, 247, 0.62)',
  height: '56%'
};
